import React, { useEffect } from "react";

import { useArticleInfoQuery } from "@api/articleInfo/useArticleInfo";
import { KEY_CLINICAL_TRIALS } from "@FillInTableContainer/components/FillInTable/constants/clinicalTrials.constants";
import { ENABLE_GROUP_TABLE_KEY_LIST } from "@FillInTableContainer/shared/constants/tableCreatorGroupTable.constants";
import {
  DEFAULT_FOCUS_STATUS,
  FocusStatus,
  useTableCreatorFocusStatus,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import {
  DEFAULT_POSITION_INFO,
  useTableCreatorPositionInfo,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import {
  useValidatedInTableCreator,
  DEFAULT_VALIDATED_INFO,
} from "@FillInTableContainer/shared/context/ValidatedInTableCreatorContext";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import { useHandleClinicalTrialsDrugProfileGroupShell } from "./useHandleClinicalTrialsDrugProfileGroupShell";
import { useHandleClinicalTrialsEndpointsDescriptionGroupShell } from "./useHandleClinicalTrialsEndpointsDescriptionGroupShell";
import { useHandleClinicalTrialsEndpointsTableGroupShell } from "./useHandleClinicalTrialsEndpointsTableGroupShell";
import { useHandleClinicalTrialsKeyCommentGroupShell } from "./useHandleClinicalTrialsKeyCommentGroupShell";
import { useHandleClinicalTrialsResultsGroupShell } from "./useHandleClinicalTrialsResultsGroupShell";

import type { ClinicalTrialsSection } from "@/types/clinicalTrials.types";

interface UseHandleGroupShellReturn {
  focusStatus: FocusStatus;
  isFocused: boolean;
  isClearGroupShell: boolean;
  isDeleteGroupShell: boolean;
  isAlertGroupShell: boolean;
  isEnableEdit: boolean;
  isLastGroupShell: boolean;
  isFocusedAddButton: boolean;
  handleDefaultFocus: () => void;
  handleFocusGroupShell: () => void;
  clickAddGroupShellButton: (
    event: React.MouseEvent<HTMLButtonElement>,
    groupShellInfo: {
      tableIndexParameter: number;
      groupShellIndexParameter: number;
      groupShellKeyParameter: string | null;
    },
  ) => void;
  handleClickToDeleteGroupShell: () => void;
}

interface Props {
  positionPropsInfo: {
    groupTableKey: ClinicalTrialsSection;
    groupTableIndex: number;
    groupShellKey: string | null;
    groupShellIndex: number | null;
    tableKey: string | null;
    tableIndex: number;
  };
  groupShellCount: number;
}

const isNotFocusGroupShell = [
  "Table Notes",
  "Clinical Results",
  "Endpoint Description",
];

export const useHandleClinicalTrialsGroupShell = ({
  positionPropsInfo,
  groupShellCount,
}: Props): UseHandleGroupShellReturn => {
  const {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    tableIndex,
  } = positionPropsInfo;

  const articleSection = "Clinical Trials";
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const { info, changeInfo } = useTableCreatorPositionInfo();
  const { validatedInfo, changeValidatedInfo } = useValidatedInTableCreator();

  const {
    handleClickToClearDrugProfileGroupShell,
    handleKeyDownToClearDrugProfileGroupShell,
  } = useHandleClinicalTrialsDrugProfileGroupShell({ positionPropsInfo });
  const {
    clickAddEndpointsTableGroupShellButton,
    handleClickToDeleteEndpointsTableGroupShell,
    handleKeyDownToDeleteEndpointsTableGroupShell,
  } = useHandleClinicalTrialsEndpointsTableGroupShell({ positionPropsInfo });
  const {
    handleClickToClearEndpointsDescriptionGroupShell,
    handleKeyDownToClearEndpointsDescriptionGroupShell,
  } = useHandleClinicalTrialsEndpointsDescriptionGroupShell({
    positionPropsInfo,
  });
  const {
    handleClickToClearKeyCommentGroupShell,
    handleKeyDownToClearKeyCommentGroupShell,
  } = useHandleClinicalTrialsKeyCommentGroupShell({ positionPropsInfo });

  const {
    handleClickToClearResultsGroupShell,
    handleKeyDownToClearResultsGroupShell,
  } = useHandleClinicalTrialsResultsGroupShell({ positionPropsInfo });

  const { data: articleData } = useArticleInfoQuery(
    Number(getPressIdFromURL()),
  );

  const isEnableEdit = articleData
    ? ENABLE_GROUP_TABLE_KEY_LIST[articleSection][groupTableKey].includes(
        articleData.draftArticleStatus,
      )
    : false;
  const isLastGroupShell = groupShellIndex === groupShellCount - 1;

  const isFocused =
    info.groupTableKey === groupTableKey &&
    info.groupTableIndex === groupTableIndex &&
    info.tableIndex === tableIndex &&
    info.groupShellKey === groupShellKey &&
    info.groupShellIndex === groupShellIndex &&
    info.shellKey === null &&
    isEnableEdit;

  const isFocusedAddButton =
    focusStatus.container === "noFocus" && isEnableEdit;

  const handleFocusGroupShell = () => {
    if (!!groupShellKey && isNotFocusGroupShell.includes(groupShellKey)) return;

    changeInfo({
      ...positionPropsInfo,
      subGroupShellIndex: null,
      subGroupShellKey: null,
      shellKey: null,
      shellIndex: null,
      valueIndex: null,
    });
    changeFocusStatus({ type: "clear", container: "groupShell" });
    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
  };

  const clickAddGroupShellButton = (
    event: React.MouseEvent<HTMLButtonElement>,
    groupShellInfo: {
      tableIndexParameter: number;
      groupShellIndexParameter: number;
      groupShellKeyParameter: string | null;
    },
  ) => {
    if (focusStatus.container !== "noFocus") return;

    if (groupTableKey === KEY_CLINICAL_TRIALS.clinicalEndpointsTable)
      clickAddEndpointsTableGroupShellButton(event, groupShellInfo);
  };

  const handleDefaultFocus = () => {
    changeInfo(DEFAULT_POSITION_INFO);
    changeFocusStatus(DEFAULT_FOCUS_STATUS);
    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
  };

  const isClearGroupShell =
    isFocused &&
    info.shellKey === null &&
    info.shellIndex === null &&
    info.valueIndex === null &&
    focusStatus.type === "clear" &&
    focusStatus.container === "groupShell";

  const isDeleteGroupShell =
    isFocused &&
    info.shellKey === null &&
    info.shellIndex === null &&
    info.valueIndex === null &&
    focusStatus.type === "delete" &&
    focusStatus.container === "groupShell";

  const isAlertGroupShell =
    validatedInfo.filter(
      (validatedItem) =>
        validatedItem.groupTableKey === groupTableKey &&
        validatedItem.groupTableIndex === groupTableIndex &&
        validatedItem.tableIndex === tableIndex &&
        validatedItem.groupShellKey === groupShellKey &&
        validatedItem.groupShellIndex === groupShellIndex &&
        validatedItem.shellKey === null &&
        validatedItem.shellIndex === null &&
        validatedItem.valueIndex === null &&
        validatedItem.type === "alert" &&
        validatedItem.container === "groupShell",
    ).length > 0;

  const handleClickToDeleteGroupShell = () => {
    switch (info.groupTableKey) {
      case KEY_CLINICAL_TRIALS.drugProfile:
        handleClickToClearDrugProfileGroupShell();
        break;
      case KEY_CLINICAL_TRIALS.clinicalEndpointsTable:
        handleClickToDeleteEndpointsTableGroupShell();
        break;
      case KEY_CLINICAL_TRIALS.clinicalEndpointsDescriptions:
        handleClickToClearEndpointsDescriptionGroupShell();
        break;
      case KEY_CLINICAL_TRIALS.keyComment:
        handleClickToClearKeyCommentGroupShell();
        break;
      case KEY_CLINICAL_TRIALS.clinicalResults:
        handleClickToClearResultsGroupShell();
        break;
      default:
        break;
    }
  };

  const handleKeyDownToDeleteGroupShell = (event: KeyboardEvent) => {
    switch (info.groupTableKey) {
      case KEY_CLINICAL_TRIALS.drugProfile:
        handleKeyDownToClearDrugProfileGroupShell(event);
        break;
      case KEY_CLINICAL_TRIALS.clinicalEndpointsTable:
        handleKeyDownToDeleteEndpointsTableGroupShell(event);
        break;
      case KEY_CLINICAL_TRIALS.clinicalEndpointsDescriptions:
        handleKeyDownToClearEndpointsDescriptionGroupShell(event);
        break;
      case KEY_CLINICAL_TRIALS.keyComment:
        handleKeyDownToClearKeyCommentGroupShell(event);
        break;
      case KEY_CLINICAL_TRIALS.clinicalResults:
        handleKeyDownToClearResultsGroupShell(event);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (focusStatus.container !== "groupShell") return;
    window.addEventListener("keydown", handleKeyDownToDeleteGroupShell);

    return () => {
      window.removeEventListener("keydown", handleKeyDownToDeleteGroupShell);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusStatus]);

  return {
    focusStatus,
    isFocused,
    isClearGroupShell,
    isDeleteGroupShell,
    isAlertGroupShell,
    isEnableEdit,
    isLastGroupShell,
    isFocusedAddButton,
    handleDefaultFocus,
    handleFocusGroupShell,
    clickAddGroupShellButton,
    handleClickToDeleteGroupShell,
  };
};
